// ============================================================================
// BlingPJPanel — Personal Finance V2 (Blueprint §5 / Fase 5+)
// Aba "Bling PJ" — mostra contas a pagar/receber enriquecidas como tabela ERP.
// Resolve contatos, formas pgto, categorias dinamicamente do snapshot.
// ============================================================================

import React, { useMemo, useState } from 'react';
import { extractBlingContas } from '../ports/blingAdapter.js';

const fmt = (val) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0);
const fmtDate = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('pt-BR') : '—';

const SIT_LABELS = {
    1: { label: 'Em aberto', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
    2: { label: 'Recebido', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    3: { label: 'Parcial', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
    4: { label: 'Devolvido', cls: 'bg-red-50 text-red-700 border-red-200' },
    5: { label: 'Cancelado', cls: 'bg-gray-50 text-gray-500 border-gray-200' },
};

function SituacaoBadge({ situacao }) {
    const s = SIT_LABELS[situacao] || { label: `Sit.${situacao}`, cls: 'bg-gray-50 text-gray-500 border-gray-200' };
    return <span className={`inline-flex items-center px-1.5 py-0.5 text-[9px] font-bold border ${s.cls}`}>{s.label}</span>;
}

export function BlingPJPanel({ blingSnapshot, blingLoading, blingError, onRefreshBling }) {
    const [subTab, setSubTab] = useState('PAGAR');

    const { pagar, receber, kpis, meta } = useMemo(() => {
        if (!blingSnapshot) return { pagar: [], receber: [], kpis: null, meta: null };
        return extractBlingContas(blingSnapshot);
    }, [blingSnapshot]);

    const items = subTab === 'PAGAR' ? pagar : receber;

    // Sort by date desc
    const sorted = useMemo(() => [...items].sort((a, b) => (a.date || '').localeCompare(b.date || '')), [items]);

    // Totals
    const totalAberto = useMemo(() => items.filter(i => i.situacao === 1).reduce((s, i) => s + i.value, 0), [items]);
    const totalBaixado = useMemo(() => items.filter(i => i.situacao !== 1).reduce((s, i) => s + i.value, 0), [items]);
    const vencidos = useMemo(() => {
        const today = new Date().toISOString().split('T')[0];
        return items.filter(i => i.situacao === 1 && i.date < today).length;
    }, [items]);

    return (
        <div className="space-y-0">
            {/* ── TOOLBAR ── */}
            <div className="flex items-center justify-between px-3 py-1.5 bg-[#f3f4f6] border-b border-[#d1d5db]">
                <div className="flex items-center gap-3">
                    <span className="text-[10px] font-bold text-[#374151] uppercase tracking-wide">
                        <i className="fas fa-building mr-1.5 text-[#6b7280]"></i>Bling PJ — Espelho Financeiro
                    </span>
                    {meta && (
                        <span className="text-[9px] text-[#9ca3af]">
                            Snapshot: {new Date(meta.generatedAt).toLocaleString('pt-BR')}
                            <span className="ml-1">({meta.counts.contasPagar} pagar, {meta.counts.contasReceber} receber)</span>
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {blingLoading && <i className="fas fa-spinner fa-spin text-[10px] text-[#6b7280]"></i>}
                    {blingError && <span className="text-[9px] text-[#dc2626]">{blingError.code === 'SNAPSHOT_NOT_FOUND' ? 'Snapshot ausente' : 'Erro snapshot'}</span>}
                    <button onClick={onRefreshBling} className="text-[9px] text-[#2563eb] hover:underline font-semibold" title="Atualizar snapshot">
                        <i className="fas fa-sync mr-0.5"></i>Atualizar
                    </button>
                </div>
            </div>

            {/* ── KPI STRIP ── */}
            {kpis && (
                <div className="flex flex-wrap border-b border-[#e5e7eb]">
                    <KpiTile label="A Receber" value={fmt(kpis.total_a_receber)} sub={`${kpis.qtd_contas_receber_total} títulos`} accent="text-[#059669]" icon="fas fa-arrow-down" />
                    <KpiTile label="A Pagar" value={fmt(kpis.total_a_pagar)} sub={`${kpis.qtd_contas_pagar_total} títulos · ${kpis.qtd_pagar_vencidos} vencidos`} accent="text-[#dc2626]" icon="fas fa-arrow-up" />
                    <KpiTile label="Saldo Projetado" value={fmt(kpis.saldo_projetado)} accent={kpis.saldo_projetado >= 0 ? 'text-[#059669]' : 'text-[#dc2626]'} icon="fas fa-chart-line" />
                    <KpiTile label="Próx. 30d Receber" value={fmt(kpis.receber_proximos_30d)} accent="text-[#2563eb]" icon="fas fa-calendar-check" />
                    <KpiTile label="Pipeline Propostas" value={fmt(kpis.valor_pipeline_propostas)} sub={`${kpis.qtd_propostas_abertas} abertas`} accent="text-[#6366f1]" icon="fas fa-briefcase" />
                    <KpiTile label="Faturamento 30d" value={fmt(kpis.faturamento_30d)} sub={`${kpis.qtd_pedidos_30d} pedidos`} accent="text-[#20243B]" icon="fas fa-receipt" />
                </div>
            )}

            {/* ── SUB-TABS ── */}
            <div className="flex items-center gap-0 bg-[#fafafa] border-b border-[#e5e7eb] px-2">
                {[
                    { id: 'PAGAR', label: 'Contas a Pagar', icon: 'fas fa-arrow-up', count: pagar.length },
                    { id: 'RECEBER', label: 'Contas a Receber', icon: 'fas fa-arrow-down', count: receber.length },
                ].map(t => (
                    <button
                        key={t.id}
                        onClick={() => setSubTab(t.id)}
                        className={`px-3 py-1.5 text-[10px] font-semibold flex items-center gap-1.5 border-b-2 transition-colors ${
                            subTab === t.id
                                ? 'text-[#20243B] border-[#20243B] bg-white'
                                : 'text-[#6b7280] border-transparent hover:text-[#374151]'
                        }`}
                    >
                        <i className={`${t.icon} text-[8px]`}></i>{t.label}
                        <span className="text-[8px] bg-[#e5e7eb] text-[#374151] px-1 py-0.5 rounded-sm font-mono">{t.count}</span>
                    </button>
                ))}
                <div className="flex-1" />
                {/* Summary chips */}
                <div className="flex items-center gap-1.5 text-[9px]">
                    <span className="px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 font-bold">
                        Em aberto: {fmt(totalAberto)}
                    </span>
                    {vencidos > 0 && (
                        <span className="px-1.5 py-0.5 bg-red-50 text-red-700 border border-red-200 font-bold">
                            <i className="fas fa-exclamation-triangle mr-0.5 text-[7px]"></i>{vencidos} vencido{vencidos > 1 ? 's' : ''}
                        </span>
                    )}
                    {totalBaixado > 0 && (
                        <span className="px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 font-bold">
                            Baixado: {fmt(totalBaixado)}
                        </span>
                    )}
                </div>
            </div>

            {/* ── DATA TABLE ── */}
            {!blingSnapshot && !blingLoading ? (
                <div className="text-center py-8 text-[#9ca3af]">
                    <i className="fas fa-cloud-download-alt text-2xl mb-2 block"></i>
                    <p className="text-[11px]">Snapshot Bling não carregado. Clique em <strong>Atualizar</strong>.</p>
                </div>
            ) : sorted.length === 0 ? (
                <div className="text-center py-8 text-[#9ca3af]">
                    <i className="fas fa-check-circle text-2xl mb-2 block text-[#059669]"></i>
                    <p className="text-[11px]">Nenhuma conta a {subTab === 'PAGAR' ? 'pagar' : 'receber'} no snapshot.</p>
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-[11px] border-collapse">
                        <thead>
                            <tr className="bg-[#f0f0f0] border-b border-[#c8c8c8]">
                                <th className="px-2 py-1 text-left text-[10px] font-semibold text-[#374151] w-[26px]">#</th>
                                <th className="px-2 py-1 text-left text-[10px] font-semibold text-[#374151] w-[80px]">Situação</th>
                                <th className="px-2 py-1 text-left text-[10px] font-semibold text-[#374151] w-[85px]">Vencimento</th>
                                <th className="px-2 py-1 text-left text-[10px] font-semibold text-[#374151]">Contato</th>
                                <th className="px-2 py-1 text-left text-[10px] font-semibold text-[#374151] w-[140px]">Forma Pgto</th>
                                {subTab === 'RECEBER' && <th className="px-2 py-1 text-left text-[10px] font-semibold text-[#374151] w-[80px]">Emissão</th>}
                                {subTab === 'RECEBER' && <th className="px-2 py-1 text-left text-[10px] font-semibold text-[#374151] w-[90px]">Conta</th>}
                                <th className="px-2 py-1 text-right text-[10px] font-semibold text-[#374151] w-[100px]">Valor</th>
                                {subTab === 'RECEBER' && <th className="px-2 py-1 text-center text-[10px] font-semibold text-[#374151] w-[40px]">Boleto</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map((item, idx) => {
                                const today = new Date().toISOString().split('T')[0];
                                const isOverdue = item.situacao === 1 && item.date < today;
                                const zebra = idx % 2 === 0 ? 'bg-white' : 'bg-[#fafafa]';
                                return (
                                    <tr key={item.blingId} className={`${zebra} ${isOverdue ? 'bg-red-50/60' : ''} border-b border-[#e5e7eb] hover:bg-[#e8f0fe]/40`}>
                                        <td className="px-2 py-1 text-[9px] text-[#9ca3af] tabular-nums">{idx + 1}</td>
                                        <td className="px-2 py-1"><SituacaoBadge situacao={item.situacao} /></td>
                                        <td className="px-2 py-1 font-mono text-[10px] text-[#374151] whitespace-nowrap">
                                            {fmtDate(item.date)}
                                            {isOverdue && <i className="fas fa-exclamation-circle text-[8px] text-red-500 ml-1" title="Vencido"></i>}
                                        </td>
                                        <td className="px-2 py-1 text-[#374151] truncate max-w-[200px]" title={item.contactName}>
                                            {item.contactName}
                                            {item.contactDoc && <span className="ml-1 text-[9px] text-[#9ca3af] font-mono">{item.contactDoc}</span>}
                                        </td>
                                        <td className="px-2 py-1 text-[10px] text-[#6b7280] truncate max-w-[140px]" title={item.formaPgto}>{item.formaPgto}</td>
                                        {subTab === 'RECEBER' && <td className="px-2 py-1 font-mono text-[10px] text-[#9ca3af]">{fmtDate(item.dataEmissao)}</td>}
                                        {subTab === 'RECEBER' && <td className="px-2 py-1 text-[10px] text-[#6b7280] truncate max-w-[90px]">{item.contaContabil || '—'}</td>}
                                        <td className={`px-2 py-1 text-right font-mono font-bold tabular-nums text-[11px] ${subTab === 'PAGAR' ? 'text-[#dc2626]' : 'text-[#059669]'}`}>
                                            {fmt(item.value)}
                                        </td>
                                        {subTab === 'RECEBER' && (
                                            <td className="px-2 py-1 text-center">
                                                {item.linkBoleto ? (
                                                    <a href={item.linkBoleto} target="_blank" rel="noopener noreferrer" className="text-[#2563eb] hover:underline" title="Abrir boleto">
                                                        <i className="fas fa-barcode text-[10px]"></i>
                                                    </a>
                                                ) : '—'}
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot className="bg-[#f0f0f0] border-t-2 border-[#c8c8c8]">
                            <tr>
                                <td colSpan={subTab === 'RECEBER' ? 7 : 5} className="px-2 py-1.5 text-right text-[10px] font-bold text-[#374151] uppercase tracking-wide">
                                    Total ({sorted.length} {sorted.length === 1 ? 'título' : 'títulos'})
                                </td>
                                <td className={`px-2 py-1.5 text-right font-mono font-bold tabular-nums text-[13px] ${subTab === 'PAGAR' ? 'text-[#dc2626]' : 'text-[#059669]'}`}>
                                    {fmt(sorted.reduce((s, i) => s + i.value, 0))}
                                </td>
                                {subTab === 'RECEBER' && <td></td>}
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}

            {/* ── STATUS BAR ── */}
            <div className="flex items-center justify-between px-3 py-1 bg-[#f0f0f0] border-t border-[#c8c8c8] text-[9px] text-[#6b7280]">
                <span>{sorted.length} {subTab === 'PAGAR' ? 'contas a pagar' : 'contas a receber'}</span>
                <span>Snapshot v{meta?.version || '?'} · Bling API</span>
            </div>
        </div>
    );
}

// Windows-style KPI tile (reused from ReconciliationPanel pattern)
function KpiTile({ label, value, sub, accent = 'text-[#20243B]', icon }) {
    return (
        <div className="bg-white border border-[#e5e7eb] px-3 py-2 min-w-[130px] flex-1">
            <div className="flex items-center gap-1.5 mb-0.5">
                {icon && <i className={`${icon} text-[9px] text-[#6b7280]`}></i>}
                <span className="text-[9px] font-semibold text-[#6b7280] uppercase tracking-wider">{label}</span>
            </div>
            <div className={`font-mono font-bold text-[14px] tabular-nums leading-tight ${accent}`}>{value}</div>
            {sub && <div className="text-[9px] text-[#9ca3af] mt-0.5">{sub}</div>}
        </div>
    );
}
